import { Dropdown, Tag } from '@carbon/react';
import { ListTable, StatTile, StatTiles, useFormat } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { ReportFrame } from './reportFrame';
import { rangeQuery, unwrap, useReportRange } from './reportRange';

type Report = HoComponents['schemas']['ControlReportDto'];
type Staff = HoComponents['schemas']['ControlStaffDto'];
type Event = HoComponents['schemas']['ControlEventDto'];

const KINDS = ['line_void', 'order_void', 'discount', 'refund', 'price_override'] as const;
const KIND_TAG: Record<string, 'red' | 'magenta' | 'purple' | 'cyan' | 'warm-gray'> = {
  line_void: 'red',
  order_void: 'magenta',
  discount: 'purple',
  refund: 'cyan',
  price_override: 'warm-gray',
};

const n = (value: number | string | null | undefined) => Number(value ?? 0);
const percent = (part: number, whole: number) => (whole === 0 ? '' : `${((part / whole) * 100).toFixed(1)}%`);

/**
 * Voids and discounts across stores (REPORTS.md B1, B2): what was voided, discounted, refunded or priced over the menu, by whom,
 * who approved it and why. Totals per kind, each staff member's share, then every event, newest first, page by page.
 */
export function ControlsReportPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const [range, setRange] = useReportRange();
  const [kind, setKind] = useState('');
  const [paging, setPaging] = useState({ page: 1, pageSize: 25 });
  const r = (key: string, options?: Record<string, unknown>) => t(`reports.controls.${key}` as I18nKey, options);

  const report = useQuery({
    queryKey: ['reports', 'controls', range, kind, paging],
    queryFn: () =>
      unwrap<Report>(
        hoApi.GET('/api/v1/reporting/controls', {
          params: {
            query: { ...rangeQuery(range), ...(kind ? { kind } : {}), page: paging.page, pageSize: paging.pageSize },
          },
        }),
      ),
    placeholderData: (previous) => previous,
  });
  const data = report.data;
  const of = (k: string) => data?.kinds.find((x) => x.kind === k);
  const voids = n(of('line_void')?.count) + n(of('order_void')?.count);
  const voidAmount = n(of('line_void')?.amount) + n(of('order_void')?.amount);
  const afterSend = n(of('line_void')?.afterSend) + n(of('order_void')?.afterSend);
  const sales = n(data?.salesTotal);
  const kindItems = [{ id: '', label: r('allKinds') }, ...KINDS.map((k) => ({ id: k, label: r(`kind.${k}`) }))];
  const tag = (k: string) => <Tag type={KIND_TAG[k] ?? 'gray'}>{r(`kind.${k}`)}</Tag>;

  const csv = () => [
    [
      r('when'),
      r('kindLabel'),
      r('store'),
      r('order'),
      r('item'),
      r('amount'),
      r('reason'),
      r('staff'),
      r('approvedBy'),
      r('afterSend'),
    ],
    ...(data?.events ?? []).map((e) => [
      e.occurredAt,
      r(`kind.${e.kind}`),
      e.storeName,
      e.orderNumber,
      e.name ?? '',
      n(e.amount),
      e.reason ?? '',
      e.user ?? '',
      e.approvedBy ?? '',
      e.afterSend,
    ]),
  ];

  return (
    <ReportFrame
      title={r('title')}
      description={r('description')}
      range={range}
      onRange={(next) => {
        setRange(next);
        setPaging((p) => ({ ...p, page: 1 }));
      }}
      error={report.error}
      csvName="voids-discounts"
      csv={(data?.events.length ?? 0) > 0 ? csv : undefined}
      filters={
        <div className="pos-report-filters__field">
          <Dropdown
            id="report-control-kind"
            titleText={r('kindLabel')}
            label={r('kindLabel')}
            items={kindItems}
            itemToString={(o) => o?.label ?? ''}
            selectedItem={kindItems.find((o) => o.id === kind) ?? kindItems[0]}
            onChange={({ selectedItem }) => {
              setKind(selectedItem?.id ?? '');
              setPaging((p) => ({ ...p, page: 1 }));
            }}
          />
        </div>
      }
    >
      <StatTiles>
        <StatTile
          label={r('voids')}
          value={format.number(voidAmount)}
          caption={r('voidsCaption', { count: voids, afterSend })}
          isLoading={report.isLoading}
        />
        <StatTile
          label={r('discounts')}
          value={format.number(n(of('discount')?.amount))}
          caption={r('shareCaption', {
            count: n(of('discount')?.count),
            share: percent(n(of('discount')?.amount), sales) || '—',
          })}
          isLoading={report.isLoading}
        />
        <StatTile
          label={r('refunds')}
          value={format.number(n(of('refund')?.amount))}
          caption={r('count', { count: n(of('refund')?.count) })}
          isLoading={report.isLoading}
        />
        <StatTile
          label={r('overrides')}
          value={format.number(n(of('price_override')?.count))}
          caption={r('approvedCaption', {
            count: KINDS.reduce((sum, k) => sum + n(of(k)?.approved), 0),
          })}
          isLoading={report.isLoading}
        />
      </StatTiles>

      <ListTable<Staff & { id: string }>
        title={r('byStaff')}
        description={r('staffNote')}
        columns={[
          { key: 'name', header: r('staff'), render: (s) => s.name || r('unknown') },
          {
            key: 'voids',
            header: r('voids'),
            numeric: true,
            render: (s) => `${format.number(n(s.voids))} · ${format.number(n(s.voidAmount))}`,
          },
          {
            key: 'discounts',
            header: r('discounts'),
            numeric: true,
            render: (s) =>
              `${format.number(n(s.discounts))} · ${format.number(n(s.discountAmount))}${n(s.sales) > 0 ? ` · ${percent(n(s.discountAmount), n(s.sales))}` : ''}`,
          },
          {
            key: 'refunds',
            header: r('refunds'),
            numeric: true,
            render: (s) => `${format.number(n(s.refunds))} · ${format.number(n(s.refundAmount))}`,
          },
          { key: 'overrides', header: r('overrides'), numeric: true, render: (s) => format.number(n(s.overrides)) },
          { key: 'sales', header: r('sales'), numeric: true, render: (s) => format.number(n(s.sales)) },
        ]}
        rows={(data?.staff ?? []).map((s, i) => ({ ...s, id: s.userId ?? `none-${i}` }))}
        ariaLabel={r('byStaff')}
        loading={report.isLoading}
        empty={{ heading: r('none') }}
      />

      <ListTable<Event & { id: string }>
        title={r('events')}
        columns={[
          { key: 'occurredAt', header: r('when'), render: (e) => format.dateTime(e.occurredAt) },
          { key: 'kind', header: r('kindLabel'), render: (e) => tag(e.kind) },
          { key: 'storeName', header: r('store') },
          { key: 'orderNumber', header: r('order') },
          {
            key: 'name',
            header: r('item'),
            render: (e) => `${e.name ?? ''}${e.quantity != null ? ` × ${format.number(n(e.quantity))}` : ''}`,
          },
          { key: 'amount', header: r('amount'), numeric: true, render: (e) => format.number(n(e.amount)) },
          {
            key: 'afterSend',
            header: r('kitchen'),
            render: (e) => (e.afterSend ? <Tag type="warm-gray">{r('afterSend')}</Tag> : ''),
          },
          {
            key: 'reason',
            header: r('reason'),
            render: (e) => e.reason ?? (e.detail ? r(`detail.${e.detail}`, { defaultValue: e.detail }) : ''),
          },
          { key: 'user', header: r('staff'), render: (e) => e.user ?? '' },
          { key: 'approvedBy', header: r('approvedBy'), render: (e) => e.approvedBy ?? '' },
        ]}
        rows={(data?.events ?? []).map((e) => ({ ...e, id: `${e.kind}-${e.eventId}` }))}
        ariaLabel={r('events')}
        loading={report.isLoading}
        empty={{ heading: r('none') }}
        pagination={{ ...paging, total: n(data?.eventCount), onChange: setPaging }}
      />
    </ReportFrame>
  );
}
